import React, { useRef, useCallback } from 'react';
import { withStyles } from '@material-ui/core';
import CommonGrid from '../../../../../../../../common/GridWithHeader/CommonGrid';
import StatusBar from '@dataGrid/components/StatusBar';
import { FILTER_POPUP_Z_INDEX } from '../../constants';
import { styles } from '../../styles';

interface GridWrapperClassParams {
  classes: Record<string, string>;
  isVisible: boolean;
  isMaximized: boolean;
}

const getGridWrapperClass = ({ classes, isVisible, isMaximized }: GridWrapperClassParams): string => {
  if (!isVisible) return `${classes.gridWrapper} ${classes.gridWrapperHidden}`;
  if (isMaximized) return `${classes.gridWrapper} ${classes.gridWrapperMaximized}`;
  return classes.gridWrapper;
};

interface DataGridProps {
  classes: Record<string, string>;
  title: string;
  exportFileName: string;
  data: any[];
  gridConfig: () => any;
  isVisible: boolean;
  isMaximized: boolean;
  gridStatus: string | null;
  statusLabel: string;
  gridHeaderConfig: Record<string, boolean>;
  onRowsSelected?: (rows: any[]) => void;
  onRowDoubleClicked?: (event: any) => void;
  onToggleGridMaximize: (isMaximized: boolean) => void;
}

const DataGrid: React.FC<DataGridProps> = ({
  classes,
  title,
  exportFileName,
  data,
  gridConfig: GridConfigFn,
  isVisible,
  isMaximized,
  gridStatus,
  statusLabel,
  gridHeaderConfig,
  onRowsSelected,
  onRowDoubleClicked,
  onToggleGridMaximize
}) => {
  const config = useRef(GridConfigFn());
  // Without this, filter menus use the short grid area and cover column headers.
  // Attaching to document.body gives enough space so the menu opens below the header.
  const popupParent = typeof document !== 'undefined' ? document.body : undefined;

  const panelLabel = useCallback(
    (filteredData: any[]) => (
      <StatusBar
        data={[{ label: `${filteredData?.length || 0} ${statusLabel}`, filterCondition: '' }]}
      />
    ),
    [statusLabel]
  );

  return (
    <div className={getGridWrapperClass({ classes, isVisible, isMaximized })}>
      <CommonGrid
        title={title}
        data={data}
        exportFileName={exportFileName}
        config={config.current}
        onRowsSelected={onRowsSelected}
        onRowDoubleClicked={onRowDoubleClicked}
        onToggleGridMaximize={onToggleGridMaximize}
        isVisible={isVisible}
        panelLabel={panelLabel}
        showErrorMessage={gridStatus}
        gridHeaderConfig={gridHeaderConfig}
        // popupParent re-parents the filter menu to document.body so it isn't clipped by the grid;
        // postProcessPopup then sets its z-index so it stays above surrounding report elements.
        popupParent={popupParent}
        postProcessPopup={(params: { ePopup?: HTMLElement }) => {
          if (params?.ePopup) params.ePopup.style.zIndex = FILTER_POPUP_Z_INDEX;
        }}
      />
    </div>
  );
};

export default withStyles(styles)(DataGrid);
